import React, { useState, useEffect } from 'react';
import { TrendingUp, Target, PenTool, Send, Eye, MessageCircle, Database, Rocket, RefreshCw, AlertCircle } from 'lucide-react';
import { apiFetch } from '../services/apiClient';

interface Lead {
  id: string;
  name?: string;
  phone?: string;
  source?: string;
  score?: number;
  note?: string;
  createdAt?: string;
}

interface GrowthWorkbenchProps {
  onLaunch?: () => void;
}

const STEPS = [
  { icon: <Target size={18} />, title: '选题', desc: '挖掘高转化小红书选题' },
  { icon: <PenTool size={18} />, title: '内容', desc: 'LLM 生成图文/脚本' },
  { icon: <Send size={18} />, title: '发布', desc: '对接 xhs-mcp 真实发布' },
  { icon: <Eye size={18} />, title: '监控', desc: '追踪曝光/互动数据' },
  { icon: <MessageCircle size={18} />, title: '微信跟进', desc: 'wxaauto 自动私信跟进' },
  { icon: <Database size={18} />, title: 'CRM', desc: '线索沉淀与打分' },
];

export function GrowthWorkbench({ onLaunch }: GrowthWorkbenchProps) {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadLeads = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/leads?limit=50');
      if (res.ok) {
        const data = await res.json();
        setLeads(Array.isArray(data) ? data : (data.leads || []));
      } else {
        setLeads([]);
      }
    } catch (e) {
      setError('线索接口暂未连接（需后端 /api/leads）');
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLeads(); }, []);

  const scoredLeads = leads.filter((l) => typeof l.score === 'number');
  const avgScore = scoredLeads.length
    ? Math.round(scoredLeads.reduce((a, l) => a + (l.score || 0), 0) / scoredLeads.length)
    : null;

  return (
    <div className="flex flex-col h-full text-white/80">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-lg">
            <TrendingUp className="text-white" size={20} />
          </div>
          <div>
            <h2 className="text-lg font-black">小红书获客工作台</h2>
            <p className="text-xs text-white/40">选题 → 内容 → 发布 → 监控 → 微信跟进 → CRM</p>
          </div>
        </div>
        <button
          onClick={onLaunch}
          className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-celestial-saturn/20 border border-celestial-saturn/30 text-celestial-saturn text-sm font-bold hover:bg-celestial-saturn/30 transition-all"
        >
          <Rocket size={16} /> 发起获客 SOP
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-5">
        {STEPS.map((s, i) => (
          <div key={i} className="flex items-start gap-2 p-3 rounded-2xl bg-white/[0.04] border border-white/10">
            <div className="mt-0.5 text-celestial-saturn">{s.icon}</div>
            <div>
              <div className="text-sm font-bold text-white/80">{i + 1}. {s.title}</div>
              <div className="text-[11px] text-white/40 leading-tight">{s.desc}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-2 mb-5">
        <div className="p-3 rounded-2xl bg-white/[0.04] border border-white/10">
          <div className="text-[11px] text-white/40">总线索</div>
          <div className="text-lg font-black text-white/85">{leads.length}</div>
        </div>
        <div className="p-3 rounded-2xl bg-white/[0.04] border border-white/10">
          <div className="text-[11px] text-white/40">已评分</div>
          <div className="text-lg font-black text-white/85">{scoredLeads.length}</div>
        </div>
        <div className="p-3 rounded-2xl bg-white/[0.04] border border-white/10">
          <div className="text-[11px] text-white/40">平均评分</div>
          <div className="text-lg font-black text-celestial-saturn">{avgScore ?? '—'}</div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar rounded-2xl bg-white/[0.03] border border-white/10 p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-bold text-white/70">CRM 线索（{leads.length}）</span>
          <button onClick={loadLeads} className="text-white/40 hover:text-white transition-colors"><RefreshCw size={14} /></button>
        </div>
        {loading ? (
          <div className="text-center py-8 text-white/30 text-xs">加载中...</div>
        ) : error ? (
          <div className="flex items-center gap-2 text-amber-400/80 text-xs py-6 justify-center"><AlertCircle size={14} />{error}</div>
        ) : leads.length === 0 ? (
          <div className="text-center py-8 text-white/25 text-xs">暂无线索，发起获客后自动沉淀</div>
        ) : (
          <div className="space-y-1.5">
            {leads.map((l) => (
              <div key={l.id} className="flex items-center justify-between p-2.5 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] transition-colors">
                <div>
                  <div className="text-sm text-white/80 font-medium">{l.name || l.phone || '未知线索'}</div>
                  <div className="text-[11px] text-white/40">{l.source || '来源未知'}{l.phone ? ` · ${l.phone}` : ''}</div>
                </div>
                {typeof l.score === 'number' && (
                  <span className="text-xs font-bold text-celestial-saturn px-2 py-0.5 rounded-full bg-celestial-saturn/10">{l.score}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
